import { useRef } from 'react'

function float32ToInt16(buffer: Float32Array): Int16Array {
  const l = buffer.length
  const int16Buffer = new Int16Array(l)
  for (let i = 0; i < l; i++) {
    const s = Math.max(-1, Math.min(1, buffer[i]))
    int16Buffer[i] = s < 0 ? s * 0x8000 : s * 0x7fff
  }
  return int16Buffer
}

export const useLiveAudioStream = () => {
  const audioContextRef = useRef<AudioContext | null>(null)
  const workletNodeRef = useRef<AudioWorkletNode | null>(null)
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const streamRef = useRef<MediaStream | null>(null)

  const onAudioChunk = useRef<(chunk: Int16Array) => void>(() => {})

  const startStream = async (handleChunk: (chunk: Int16Array) => void) => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    streamRef.current = stream

    const audioContext = new AudioContext({ sampleRate: 48000 })
    audioContextRef.current = audioContext

    await audioContext.audioWorklet.addModule('/pcm-processor.js')

    const source = audioContext.createMediaStreamSource(stream)
    sourceRef.current = source

    const workletNode = new AudioWorkletNode(audioContext, 'pcm-processor', {
      processorOptions: { sampleRate: audioContext.sampleRate },
    })
    workletNodeRef.current = workletNode

    onAudioChunk.current = handleChunk

    const accumulatedSamples: number[] = []

    workletNode.port.onmessage = (event: MessageEvent<Float32Array>) => {
      const pcm = float32ToInt16(event.data)

      accumulatedSamples.push(...pcm)

      const chunkSize = 800
      while (accumulatedSamples.length >= chunkSize) {
        const chunk = accumulatedSamples.splice(0, chunkSize)
        onAudioChunk.current(new Int16Array(chunk))
      }
    }

    source.connect(workletNode)

    // * Resume audio context if it's suspended (needed for Firefox)
    if (audioContext.state === 'suspended') await audioContext.resume()

    // * Give Firefox time to stabilize worklet audio flow
    await new Promise((resolve) => setTimeout(resolve, 100))
  }

  const stopStream = () => {
    workletNodeRef.current?.disconnect()
    sourceRef.current?.disconnect()
    audioContextRef.current?.close()

    streamRef.current?.getTracks().forEach((track) => track.stop())

    audioContextRef.current = null
    workletNodeRef.current = null
    sourceRef.current = null
    streamRef.current = null
  }

  return { startStream, stopStream }
}
